import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  IconButton,
  Typography,
  Grid,
  TableFooter,
  TableRow,
  TableCell,
  Paper,
  Button
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import LambdaFetch from '../functions/FetchFromLambda'
import LoadingButton from '../components/LoadingButton'
import { trialBalanceCols } from '../constants/GlCols'
import TableMui from '../components/TableMui'
import getTableOptions from '../constants/TableOptions'
import { smallScreenWidth } from '../constants/AppConstants'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import { DashboardAthenaTable } from '../components/DashboardAthenaTable'
import OpenSearchDropdown from '../components/OpenSearchDropdown'
import HardCodedDropdown from '../components/HardCodedDropdown'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    margin: '2rem 1rem'
  },
  footerCell: {
    backgroundColor: '#F5F5F5',
    borderBottom: 'none',
    zIndex: 999
  },
  stickyFooterCell: {
    position: 'sticky',
    bottom: '-1px',
    zIndex: 100
  },
  rightTableHead: {
    '& > span': {
      justifyContent: 'flex-end',
      marginRight: '0'
    }
  }
}))

const defaultHeaders = {
  fiscalYear: '',
  company: '',
  acctUnit: '',
  account: '',
  subAccount: '',
  period: '',
  hideZeroRows: 0
}

export default function TrialBalanceServerSide(props) {
  const [state, setstate] = useState({
    options: null,
    isLoading: false,
    data: null,
    fetched: false
  })
  const [headers, setHeaders] = useState(defaultHeaders)
  const [showTable, setShowTable] = useState(false)
  const [clearHeader, setClearHeaders] = React.useState(false)
  const [clearTrigger, setClearTrigger] = React.useState(false)

  const classes = useStyles()

  const { credentials } = props.fetchInitialData

  const handleSubmit = async e => {
    e.preventDefault()
    if (JSON.stringify(headers) === state.tableKey) return null
    setShowTable(true)
    setstate({ ...state, isLoading: true, tableKey: JSON.stringify(headers) })
  }

  const handleCallback = (values, error) => {
    if (error) {
      setstate({ ...state, isLoading: false })
      setShowTable(false)
      return null
    }
    setstate({ ...state, isLoading: false })
  }

  return (
    <div style={{ padding: '1.5rem 0.5rem' }}>
      <IconButton
        style={{ borderRadius: '5px', position: 'absolute' }}
        size='small'
        onClick={() => props.history.push('/')}
        aria-label='delete'
      >
        <ArrowBackIcon />
        {props.fetchInitialData.credentials.appWidth > smallScreenWidth &&
          'Dashboard'}
      </IconButton>
      <Typography variant='h5' style={{ textAlign: 'center' }}>
        GL Trial Balance
      </Typography>
      <div className={classes.root}>
        <Paper elevation={0} style={{ padding: '1rem' }}>
          <form onSubmit={handleSubmit}>
            <Grid
              container
              alignContent='center'
              alignItems='flex-end'
              spacing={3}
            >
              <Grid item>
                <OpenSearchDropdown
                  id='company'
                  required
                  width='125px'
                  label='Company'
                  page="GLTRANS"
                  entity="COMPANY"
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
                  onChange={(val) => {
                    setHeaders({ ...headers, company: val?.VALUE || '' })
                  }}
                />
              </Grid>
              <Grid item>
                <OpenSearchDropdown
                  id='fiscalYear'
                  required
                  width='125px'
                  label='Year'
                  page="GLTRIALBALANCE"
                  entity="YEAR"
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
                  onChange={(val) => {
                    setHeaders({ ...headers, fiscalYear: val?.VALUE || '' })
                  }}
                />
              </Grid>
              <Grid item>
                <HardCodedDropdown
                  id='period'
                  required
                  width='125px'
                  label='Period'
                  dataset="PERIOD"
                  clearTrigger={clearTrigger}
                  onChange={(val) => {
                    setHeaders({ ...headers, period: val?.VALUE || '' })
                  }}
                />
              </Grid>
              <Grid item>
                <OpenSearchDropdown
                  id='acctUnit'
                  required={false}
                  disabled={!headers.fiscalYear || !headers.period || !headers.company}
                  width='125px'
                  label='Account Unit'
                  page="GLTRANS"
                  entity="ACCTUNIT"
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
                  filters={{
                    YEAR: headers.fiscalYear,
                    COMPANY: headers.company,
                    PERIOD: headers.period
                  }}
                  onChange={(val) => {
                    setHeaders({ ...headers, acctUnit: val?.VALUE || '' })
                  }}
                />
              </Grid>
              <Grid item>
                <OpenSearchDropdown
                  id='account'
                  required={false}
                  disabled={!headers.fiscalYear || !headers.period || !headers.company}
                  width='125px'
                  label='Account'
                  page="GLTRANS"
                  entity="ACCOUNT"
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
                  filters={{
                    YEAR: headers.fiscalYear,
                    COMPANY: headers.company,
                    ACCTUNIT: headers.accountUnit,
                  }}
                  onChange={(val) => {
                    setHeaders({ ...headers, account: val?.VALUE || '' })
                  }}
                />
              </Grid>
              <Grid item>
                <OpenSearchDropdown
                  id='subAccount'
                  required={false}
                  disabled={!headers.fiscalYear || !headers.period || !headers.company}
                  width='125px'
                  label='Sub Account'
                  page="GLTRANS"
                  entity="SUBACCOUNT"
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
                  filters={{
                    YEAR: headers.fiscalYear,
                    COMPANY: headers.company,
                    ACCTUNIT: headers.accountUnit,
                    ACCOUNT: headers.account
                  }}
                  onChange={(val) => {
                    setHeaders({ ...headers, subAccount: val?.VALUE || '' })
                  }}
                />
              </Grid>

              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={headers.hideZeroRows}
                      onChange={e => {
                        setHeaders({
                          ...headers,
                          hideZeroRows: e.target.checked ? 1 : 0
                        })
                      }}
                      name='active'
                      color='primary'
                    />
                  }
                  label='Hide Zero Rows'
                />
              </Grid>
            </Grid>

            <div style={{ marginTop: '1rem' }}>
              <LoadingButton
                label='Submit'
                disabled={!(headers.fiscalYear && headers.acctPeriod)}
                isLoading={state.isLoading}
                color='primaryVLButton'
                buttonType='submit'
              />
              {!!(
                headers.company ||
                headers.fiscalYear ||
                headers.period ||
                headers.acctUnit ||
                headers.account ||
                headers.subAccount ||
                headers.hideZeroRows === 1
              ) && (
                <Button
                  onClick={() => {
                    setClearHeaders(!clearHeader)
                    setHeaders(defaultHeaders)
                    setShowTable(false)
                    setClearTrigger(prev => !prev)
                  }}
                  style={{ marginLeft: '0.5rem' }}
                >
                  Clear
                </Button>
              )}
            </div>
          </form>
        </Paper>

        {/* {!!showTable && (
          <div style={{ marginTop: '1rem ' }}>
            <ReactTableServerSide
              {...props}
              key={state.tableKey}
              params={{ ...headers }}
              action='get-trial-balances-serverside'
              apiResource={'trial-balance'}
              transform={r => r}
              tableTitle={''}
              tableColumns={trialBalanceCols}
              callback={handleCallback}
              noPagination
              noSort
              downloadAction={'get-trial-balances-serverside-download'}
              initialState={{
                columnVisibility: { CHART: false, FY: false, PRD: false }
              }}
            />
          </div>
        )} */}


        {showTable && (
          <div style={{ marginTop: '1rem ' }}>


            {/* <DashboardAthenaTableServerSideNoSelect
              {...props}
              key={state.tableKey}
              params={{ ...headers }}
              tableColumns={trialBalanceCols}
              reportName='gl_trial_balance_serverside_temp'
              tableTitle={''}
              callback={handleCallback}
              transform={tableDataTransformation}
              downloadFileName={'GL_Trans_Download'}
            /> */}



            <DashboardAthenaTable
              {...props}
              isTempTable={true}
              key={state.tableKey}
              params={{ ...headers }}
              tableColumns={trialBalanceCols}
              reportParamName="gl_trial_balance_serverside"
              reportName='gl_trial_balance_serverside_temp'
              tableTitle={''}
              callback={handleCallback}
              downloadFileName={'Trial_Balance_Download'}
            />


          </div>
        )}
      </div>
    </div>
  )
}
